<template>
  <v-card class="card">
    <v-card-title class="d-flex align-center">
      <span>Automated Email Settings</span>
      <v-spacer></v-spacer>
      <v-chip
        :color="settings.enabled ? 'success' : 'grey'"
        variant="flat"
        size="small"
      >
        {{ settings.enabled ? 'Enabled' : 'Disabled' }}
      </v-chip>
    </v-card-title>

    <v-card-text v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2">Loading settings...</div>
    </v-card-text>

    <v-card-text v-else>
      <v-form ref="formRef" @submit.prevent="saveSettings">
        <v-switch
          v-model="settings.enabled"
          label="Enable automated follow-up emails"
          color="primary"
          hide-details
          class="mb-4"
        ></v-switch>

        <v-divider class="mb-4"></v-divider>

        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="senderEmailPrefix"
              label="Sender Email"
              :rules="[rules.required, rules.validEmailPrefix]"
              :suffix="`@${brand.emails.domain}`"
              variant="outlined"
              density="compact"
              @update:model-value="onSenderEmailChange"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="9">
            <v-text-field
              v-model="settings.emailSubject"
              label="Email Subject"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
              counter="78"
              maxlength="78"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-textarea
          v-model="settings.emailBody"
          label="Email Body"
          :rules="[rules.required]"
          variant="outlined"
          rows="10"
          class="mt-4"
        ></v-textarea>

        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          <strong>Template Variables:</strong> Use <code>{firstName}</code> to insert the user's first name.
          If the user has no first name, "there" will be used instead.
        </v-alert>

        <v-divider class="my-4"></v-divider>

        <div class="d-flex justify-end">
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="saveSettings"
          >
            Save Settings
          </v-btn>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useStore } from 'vuex'
import client from '@/graphql/client'
import GetAutomatedEmailSettings from '@/graphql/queries/GetAutomatedEmailSettings'
import UpdateAutomatedEmailSettings from '@/graphql/mutations/UpdateAutomatedEmailSettings'
import { useBrand } from '@/composables/useBrand'

const store = useStore()
const brand = useBrand()
const formRef = ref(null)
const loading = ref(true)
const saving = ref(false)
const senderEmailPrefix = ref('')

const settings = reactive({
  enabled: false,
  senderEmail: '',
  emailSubject: '',
  emailBody: ''
})

const rules = {
  required: (v) => !!v || 'This field is required',
  validEmailPrefix: (v) => {
    if (!v) return 'This field is required'
    if (v.includes('@')) return 'Do not include @ symbol'
    const validPattern = /^[a-zA-Z0-9._+-]+$/
    if (!validPattern.test(v)) return 'Only letters, numbers, dots, hyphens, underscores, and plus signs allowed'
    if (v.startsWith('.') || v.endsWith('.')) return 'Cannot start or end with a dot'
    if (v.includes('..')) return 'Cannot have consecutive dots'
    return true
  }
}

const extractSenderEmailPrefix = () => {
  if (settings.senderEmail) {
    const atIndex = settings.senderEmail.indexOf('@')
    if (atIndex > 0) {
      senderEmailPrefix.value = settings.senderEmail.substring(0, atIndex)
    } else {
      senderEmailPrefix.value = settings.senderEmail
    }
  }
}

const onSenderEmailChange = () => {
  const prefix = senderEmailPrefix.value.replace(/@/g, '')
  senderEmailPrefix.value = prefix
  settings.senderEmail = prefix ? `${prefix}@${brand.emails.domain}` : ''
}

const loadSettings = async () => {
  loading.value = true
  try {
    const response = await client.query({
      operationName: 'getAutomatedEmailSettings',
      query: GetAutomatedEmailSettings
    })
    if (response.getAutomatedEmailSettings) {
      Object.assign(settings, response.getAutomatedEmailSettings)
      extractSenderEmailPrefix()
    }
  } catch (error) {
    console.error('Failed to load settings:', error)
    store.commit('snackbar/showMessage', { content: 'Failed to load settings', color: 'error' }, { root: true })
  } finally {
    loading.value = false
  }
}

const saveSettings = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    store.commit('snackbar/showMessage', { content: 'Please fix the errors before saving', color: 'error' }, { root: true })
    return
  }

  saving.value = true
  try {
    const response = await client.query({
      operationName: 'updateAutomatedEmailSettings',
      query: UpdateAutomatedEmailSettings,
      variables: {
        input: {
          enabled: settings.enabled,
          senderEmail: settings.senderEmail,
          emailSubject: settings.emailSubject,
          emailBody: settings.emailBody
        }
      }
    })
    if (response.updateAutomatedEmailSettings) {
      Object.assign(settings, response.updateAutomatedEmailSettings)
      extractSenderEmailPrefix()
      store.commit('snackbar/showMessage', { content: 'Settings saved successfully', color: 'success' }, { root: true })
    }
  } catch (error) {
    console.error('Failed to save settings:', error)
    store.commit('snackbar/showMessage', { content: 'Failed to save settings', color: 'error' }, { root: true })
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadSettings()
})
</script>

<style scoped>
.card {
  padding: 16px;
}

code {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}
</style>
